import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getBalance, getHistory } from '../utils/api';

export default function DashboardPage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Dashboard';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const { user, logout }        = useAuth();
  const navigate                = useNavigate();
  const [balance, setBalance]   = useState(null);
  const [history, setHistory]   = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => { loadData(); }, []);



  async function loadData() {
    try {
      const [balRes, histRes] = await Promise.all([getBalance(), getHistory()]);
      setBalance(balRes.data);
      setHistory(histRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  function getInitials(name) {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }

  function formatDate(ts) {
    return new Date(ts * 1000).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  }

  const txConfig = {
    deposit:          { label: 'Added funds',        color: '#00C48C', sign: '+',  bg: '#E6FAF5' },
    send:             { label: 'Sent',               color: '#FF3B30', sign: '-',  bg: '#FFF0EF' },
    receive:          { label: 'Received',           color: '#00C48C', sign: '+',  bg: '#E6FAF5' },
    cashout_spend:    { label: 'Cashed out',         color: '#FF3B30', sign: '-',  bg: '#FFF0EF' },
    cashout_save:     { label: 'Cashed out Earning', color: '#FF3B30', sign: '-',  bg: '#FFF0EF' },
    payment:          { label: 'Payment sent',       color: '#FF3B30', sign: '-',  bg: '#FFF0EF' },
    payment_received: { label: 'Payment received',   color: '#00C48C', sign: '+',  bg: '#E6FAF5' },
    spend_to_stack:   { label: 'Moved to Earning',   color: '#007AFF', sign: '↓',  bg: '#EBF4FF' },
    stack_to_spend:   { label: 'Moved to Spend',     color: '#007AFF', sign: '↑',  bg: '#EBF4FF' },
    spend_to_save:    { label: 'Moved to Earning',   color: '#007AFF', sign: '↓',  bg: '#EBF4FF' },
    save_to_spend:    { label: 'Moved to Spend',     color: '#007AFF', sign: '↑',  bg: '#EBF4FF' },
    boost_reward:     { label: 'Boost reward',       color: '#FF9500', sign: '⚡', bg: '#FFF5E6' },
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.headerLeft}>
          <div style={s.avatar}>{user ? getInitials(user.name) : '?'}</div>
          <div>
            <div style={s.greeting}>Good {getGreeting()}</div>
            <div style={s.name}>{user?.name?.split(' ')[0]}</div>
          </div>
        </div>
        <button style={s.logoutBtn} onClick={logout}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6E6E73" strokeWidth="1.8">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
          </svg>
        </button>
      </div>

      {loading ? (
        <div style={s.loading}>
          <div style={s.skeleton}/>
          <div style={{...s.skeleton, width:'60%', marginTop:8}}/>
        </div>
      ) : (
        <>
          <div style={s.balanceCard}>
            <div style={s.balanceLabel}>Total Balance</div>
            <div style={s.balanceAmount}>${balance?.totalUsd || '0.00'}</div>

            <div style={s.bucketsRow}>
              <div style={s.bucket}>
                <div style={s.bucketIcon}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="2">
                    <circle cx="12" cy="12" r="9"/>
                    <path d="M12 6v6l4 2"/>
                  </svg>
                </div>
                <div style={s.bucketInfo}>
                  <div style={s.bucketName}>Spend</div>
                  <div style={s.bucketAmount}>${balance?.spend?.usd || '0.00'}</div>
                </div>
              </div>

              <div style={s.bucketDivider}/>

              <div style={s.bucket}>
                <div style={{...s.bucketIcon, background:'#EBF4FF'}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="2">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                  </svg>
                </div>
                <div style={s.bucketInfo}>
                  <div style={s.bucketName}>
                    Earning
                    <span style={s.liveDot}/>
                  </div>
                  <div style={s.bucketAmount}>${balance?.save?.usd || '0.00'}</div>
                </div>
              </div>
            </div>
          </div>

          <div style={s.actionsRow}>
            {[
              { label: 'Cash out',     icon: '↓', path: '/cashout',      color: '#00C48C' },
              { label: 'Transactions', icon: '☰', path: '/transactions', color: '#6E6E73' },
            ].map(a => (
              <button key={a.label} style={s.actionBtn}
                onClick={() => a.path && navigate(a.path)}>
                <div style={{...s.actionIcon, background: a.color+'15', color: a.color}}>
                  {a.icon}
                </div>
                <span style={s.actionLabel}>{a.label}</span>
              </button>
            ))}
          </div>

          <div style={s.section}>
            <div style={s.sectionHeader}>
              <span style={s.sectionTitle}>Recent Activity</span>
            </div>

            {history.length === 0 ? (
              <div style={s.empty}>
                <div style={s.emptyIcon}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#AEAEB2" strokeWidth="1.5">
                    <rect x="2" y="5" width="20" height="14" rx="3"/>
                    <path d="M2 10h20"/>
                  </svg>
                </div>
                <div style={s.emptyText}>No transactions yet</div>
                <div style={s.emptySub}>Add funds to get started</div>
              </div>
            ) : (
              <div style={s.txList}>
                {history.map(tx => {
                  const cfg = txConfig[tx.type] || { label: tx.type, color: '#6E6E73', sign: '', bg: '#F5F5F7' };
                  return (
                    <div key={tx.id} style={s.txItem}>
                      <div style={{...s.txDot, background: cfg.bg, color: cfg.color}}>
                        {cfg.sign}
                      </div>
                      <div style={s.txInfo}>
                        <div style={s.txLabel}>
                          {tx.metadata?.source === 'wave'
                            ? 'Wave Invoice #' + (tx.metadata.waveInvoiceNumber || '')
                            : cfg.label}
                          {tx.contactName ? ' — ' + tx.contactName : ''}
                        </div>
                        <div style={s.txDate}>{formatDate(tx.createdAt)}</div>
                      </div>
                      <div style={{...s.txAmount, color: cfg.color}}>
                        {cfg.sign}${tx.netUsd}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

const s = {
  page:          { minHeight: '100vh', background: '#F5F5F7', paddingBottom: 88 },
  header:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '56px 20px 20px' },
  headerLeft:    { display: 'flex', alignItems: 'center', gap: 12 },
  avatar:        { width: 40, height: 40, borderRadius: '50%', background: '#007AFF', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600 },
  greeting:      { fontSize: 12, color: '#6E6E73' },
  name:          { fontSize: 16, fontWeight: 600, color: '#1D1D1F' },
  logoutBtn:     { background: 'none', border: 'none', padding: 8 },
  loading:       { padding: '24px 20px' },
  skeleton:      { height: 20, borderRadius: 8, background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', backgroundSize: '400px 100%', animation: 'shimmer 1.5s infinite' },
  balanceCard:   { margin: '0 16px 16px', background: '#fff', borderRadius: 24, padding: '24px 20px', boxShadow: '0 2px 20px rgba(0,0,0,0.06)' },
  balanceLabel:  { fontSize: 13, color: '#6E6E73', marginBottom: 4 },
  balanceAmount: { fontSize: 44, fontWeight: 600, letterSpacing: -1.5, marginBottom: 20 },
  bucketsRow:    { display: 'flex', gap: 0 },
  bucket:        { flex: 1, display: 'flex', alignItems: 'flex-start', gap: 8 },
  bucketDivider: { width: 1, background: '#F5F5F7', margin: '0 16px' },
  bucketIcon:    { width: 28, height: 28, borderRadius: 8, background: '#EBF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 },
  bucketInfo:    { flex: 1 },
  bucketName:    { fontSize: 12, color: '#6E6E73', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 },
  bucketAmount:  { fontSize: 17, fontWeight: 600 },
  liveDot:       { width: 6, height: 6, borderRadius: '50%', background: '#00C48C', display: 'inline-block', animation: 'pulse 2s infinite' },
  actionsRow:    { display: 'flex', gap: 8, margin: '0 16px 16px' },
  actionBtn:     { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, background: '#fff', border: 'none', borderRadius: 16, padding: '14px 8px', boxShadow: '0 2px 20px rgba(0,0,0,0.06)' },
  actionIcon:    { width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 500 },
  actionLabel:   { fontSize: 12, fontWeight: 500, color: '#1D1D1F' },
  section:       { margin: '0 16px' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  sectionTitle:  { fontSize: 15, fontWeight: 600 },
  empty:         { background: '#fff', borderRadius: 20, padding: '40px 20px', textAlign: 'center', boxShadow: '0 2px 20px rgba(0,0,0,0.06)' },
  emptyIcon:     { marginBottom: 12, display: 'flex', justifyContent: 'center' },
  emptyText:     { fontSize: 15, fontWeight: 500, marginBottom: 4 },
  emptySub:      { fontSize: 13, color: '#6E6E73' },
  txList:        { background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 2px 20px rgba(0,0,0,0.06)' },
  txItem:        { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid #F5F5F7' },
  txDot:         { width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, flexShrink: 0 },
  txInfo:        { flex: 1 },
  txLabel:       { fontSize: 14, fontWeight: 500 },
  txDate:        { fontSize: 12, color: '#AEAEB2', marginTop: 2 },
  txAmount:      { fontSize: 14, fontWeight: 600 },
};
